import { createFileRoute } from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  accountPreferencesQueryOptions,
  isUnreadablePreferences,
  modelAgentsQueryOptions,
  modelProvidersQueryOptions,
  ontologyVersionsQueryOptions,
} from '#/api/preferences'
import { AccountSettingsForm } from '#/components/account-settings/account-settings-form'
import { PageHeader } from '#/components/page-header'
import { RouteError } from '#/components/route-error'
import { RoutePending } from '#/components/route-pending'
import { hasPermission, useCan } from '#/user/me'
import { PageContainer } from '#/components/page-container'

export const Route = createFileRoute('/_authed/accounts/$accountId/settings')({
  loader: ({ context, params }) => {
    // Model-provider/ontology/agent/preferences config is admin-scoped — the
    // BFF 403s it without accounts:configure, and only the gated forms read it.
    // Skip the prefetch for members, who just see the no-access notice.
    if (!hasPermission(context.me, 'annwn:accounts:configure')) return
    return Promise.all([
      context.queryClient.ensureQueryData(
        accountPreferencesQueryOptions(params.accountId),
      ),
      context.queryClient.ensureQueryData(modelProvidersQueryOptions),
      context.queryClient.ensureQueryData(ontologyVersionsQueryOptions),
      context.queryClient.ensureQueryData(modelAgentsQueryOptions),
    ])
  },
  pendingComponent: RoutePending,
  errorComponent: SettingsError,
  component: AccountSettings,
})

// The BFF 500s this endpoint when the stored preferences blob holds a value it
// no longer accepts — a deprecated `ontology_version` being the way that
// happens. Neither GET nor PATCH can get past it, so there is nothing to retry
// and no in-app fix: name the situation instead of showing a bare failure with a
// Retry that can't work. Anything else falls through to the usual screen.
function SettingsError(props: ErrorComponentProps) {
  const { t } = useTranslation()
  if (!isUnreadablePreferences(props.error)) return <RouteError {...props} />
  return (
    <PageContainer>
      <PageHeader title={t('accountSettings.title')} />
      <p role="alert" className="text-sm text-destructive">
        {t('accountSettings.preferencesUnreadable')}
      </p>
    </PageContainer>
  )
}

function AccountSettings() {
  const { t } = useTranslation()
  const { accountId } = Route.useParams()
  const canConfigure = useCan('annwn:accounts:configure')

  return (
    <PageContainer>
      <PageHeader title={t('accountSettings.title')} />
      {canConfigure ? (
        <AccountSettingsForm accountId={accountId} />
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('accountSettings.noAccess')}
        </p>
      )}
    </PageContainer>
  )
}
